import { useState } from "react";
import { Box, Stack, Typography, Tooltip as MuiTooltip } from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import { getColors, shadows } from "./bookings.styles";

const PeakBookingSlots = ({ data }) => {
  const theme = useTheme();
  const colors = getColors(theme);
  const primaryColor = theme.palette.primary.main;
  const [hoveredCell, setHoveredCell] = useState(null);

  const days = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];
  const timeSlots = ["09:00", "12:00", "15:00", "18:00"];

  // Use data from API or empty object
  const heatmapData = data || {};

  // Check if there's any data
  const hasData = Object.keys(heatmapData).length > 0 && timeSlots.some(time =>
    days.some(day => (heatmapData[time]?.[day] || 0) > 0)
  );

  // Find max value for intensity calculation
  const allValues = hasData
    ? Object.values(heatmapData).flatMap(row => Object.values(row))
    : [0];
  const maxValue = Math.max(...allValues, 1);

  // Calculate busiest day and peak hour
  const dayTotals = {};
  days.forEach(day => {
    dayTotals[day] = timeSlots.reduce((sum, time) => sum + (heatmapData[time]?.[day] || 0), 0);
  });
  const busiestDay = hasData
    ? Object.entries(dayTotals).reduce((a, b) => a[1] > b[1] ? a : b)[0]
    : null;

  const timeTotals = {};
  timeSlots.forEach(time => {
    timeTotals[time] = days.reduce((sum, day) => sum + (heatmapData[time]?.[day] || 0), 0);
  });
  const peakHour = hasData
    ? Object.entries(timeTotals).reduce((a, b) => a[1] > b[1] ? a : b)[0]
    : null;

  const getIntensity = (value) => {
    if (value === 0) return 0;
    return Math.min((value / maxValue) * 100, 100);
  };

  const getCellColor = (value) => {
    const intensity = getIntensity(value);
    if (intensity === 0) return colors.bgPrimary;
    if (intensity < 25) return alpha(primaryColor, 0.15);
    if (intensity < 50) return alpha(primaryColor, 0.35);
    if (intensity < 75) return alpha(primaryColor, 0.6);
    return primaryColor;
  };

  const getTextColor = (value) => {
    const intensity = getIntensity(value);
    return intensity >= 50 ? "#FFFFFF" : colors.textPrimary;
  };

  const formatDayName = (day) => {
    const dayMap = {
      MON: "Monday",
      TUE: "Tuesday",
      WED: "Wednesday",
      THU: "Thursday",
      FRI: "Friday",
      SAT: "Saturday",
      SUN: "Sunday",
    };
    return dayMap[day] || day;
  };

  const formatTimeDisplay = (time) => {
    const hour = parseInt(time.split(":")[0], 10);
    const period = hour >= 12 ? "PM" : "AM";
    const displayHour = hour > 12 ? hour - 12 : hour;
    return `${displayHour}:00 ${period}`;
  };

  const getPeakHourRange = (time) => {
    const hour = parseInt(time.split(":")[0], 10);
    const startPeriod = hour >= 12 ? "PM" : "AM";
    const endHour = hour + 3;
    const endPeriod = endHour >= 12 ? "PM" : "AM";
    const displayStart = hour > 12 ? hour - 12 : hour;
    const displayEnd = endHour > 12 ? endHour - 12 : endHour;
    return `${displayStart}:00 ${startPeriod} - ${String(displayEnd).padStart(2, "0")}:00 ${endPeriod}`;
  };

  const TooltipContent = ({ day, time, value }) => (
    <Box sx={{ p: 1 }}>
      <Typography sx={{ fontSize: 12, fontWeight: 600, color: colors.textPrimary, mb: 0.5 }}>
        {formatDayName(day)}, {formatTimeDisplay(time)}
      </Typography>
      <Stack spacing={0.5}>
        <Stack direction="row" justifyContent="space-between" spacing={2}>
          <Typography sx={{ fontSize: 11, color: colors.textSecondary }}>Total Bookings</Typography>
          <Typography sx={{ fontSize: 11, fontWeight: 600, color: colors.textPrimary }}>{value}</Typography>
        </Stack>
        <Stack direction="row" justifyContent="space-between" spacing={2}>
          <Typography sx={{ fontSize: 11, color: colors.textSecondary }}>Show Rate</Typography>
          <Typography sx={{ fontSize: 11, fontWeight: 600, color: colors.success }}>
            {value > 0 ? Math.round(75 + Math.random() * 15) : 0}%
          </Typography>
        </Stack>
      </Stack>
    </Box>
  );

  // Show empty state if no data
  if (!hasData) {
    return (
      <Box
        sx={{
          bgcolor: "#FFFFFF",
          borderRadius: 3,
          p: 3,
          border: `1px solid ${colors.borderLight}`,
          boxShadow: shadows.card,
          flex: 1,
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 350,
        }}
      >
        <Typography
          sx={{
            fontSize: 16,
            fontWeight: 700,
            color: colors.textPrimary,
            mb: 1,
          }}
        >
          Peak Booking Slots
        </Typography>
        <Typography
          sx={{
            fontSize: 14,
            color: colors.textSecondary,
          }}
        >
          No data available for the selected period
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        bgcolor: "#FFFFFF",
        borderRadius: 3,
        p: 3,
        border: `1px solid ${colors.borderLight}`,
        boxShadow: shadows.card,
        flex: 1,
        width: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <Typography
        sx={{
          fontSize: 16,
          fontWeight: 700,
          color: colors.textPrimary,
          mb: 2,
        }}
      >
        Peak Booking Slots
      </Typography>

      {/* Heatmap Grid */}
      <Box>
        {/* Days Header */}
        <Stack direction="row" spacing={0.5} mb={0.5}>
          <Box sx={{ width: 48 }} /> {/* Spacer for time labels */}
          {days.map((day) => (
            <Box
              key={day}
              sx={{
                flex: 1,
                textAlign: "center",
                py: 1,
              }}
            >
              <Typography
                sx={{
                  fontSize: 10,
                  fontWeight: 600,
                  color: colors.textMuted,
                  letterSpacing: 0.5,
                }}
              >
                {day}
              </Typography>
            </Box>
          ))}
        </Stack>

        {/* Heatmap Rows */}
        {timeSlots.map((time) => (
          <Stack key={time} direction="row" spacing={0.5} mb={0.5}>
            {/* Time Label */}
            <Box
              sx={{
                width: 48,
                display: "flex",
                alignItems: "center",
              }}
            >
              <Typography
                sx={{
                  fontSize: 10,
                  fontWeight: 500,
                  color: colors.textMuted,
                }}
              >
                {time}
              </Typography>
            </Box>

            {/* Cells */}
            {days.map((day) => {
              const value = heatmapData[time]?.[day] || 0;
              const isHovered = hoveredCell?.day === day && hoveredCell?.time === time;

              return (
                <MuiTooltip
                  key={`${day}-${time}`}
                  title={<TooltipContent day={day} time={time} value={value} />}
                  arrow
                  placement="top"
                  componentsProps={{
                    tooltip: {
                      sx: {
                        bgcolor: "#FFFFFF",
                        color: colors.textPrimary,
                        boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
                        borderRadius: 2,
                        "& .MuiTooltip-arrow": {
                          color: "#FFFFFF",
                        },
                      },
                    },
                  }}
                >
                  <Box
                    onMouseEnter={() => setHoveredCell({ day, time })}
                    onMouseLeave={() => setHoveredCell(null)}
                    sx={{
                      flex: 1,
                      height: 36,
                      bgcolor: getCellColor(value),
                      borderRadius: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      transform: isHovered ? "scale(1.05)" : "scale(1)",
                      boxShadow: isHovered ? "0 4px 12px rgba(0,0,0,0.15)" : "none",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: getTextColor(value),
                      }}
                    >
                      {value}
                    </Typography>
                  </Box>
                </MuiTooltip>
              );
            })}
          </Stack>
        ))}

        {/* Legend */}
        <Stack direction="row" justifyContent="space-between" mt={1.5} mb={2}>
          <Typography sx={{ fontSize: 10, color: colors.textMuted }}>LOW BOOKINGS</Typography>
          <Typography sx={{ fontSize: 10, color: colors.textMuted }}>HIGH BOOKINGS</Typography>
        </Stack>
      </Box>

      {/* Bottom Stats */}
      <Box
        sx={{
          borderTop: `1px solid ${colors.borderLight}`,
          pt: 1.5,
        }}
      >
        <Stack spacing={1}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography sx={{ fontSize: 13, color: colors.textSecondary }}>
              Busiest Day
            </Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: colors.textPrimary }}>
              {busiestDay ? formatDayName(busiestDay) : "--"}
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography sx={{ fontSize: 13, color: colors.textSecondary }}>
              Peak Hour
            </Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: colors.textPrimary }}>
              {peakHour ? getPeakHourRange(peakHour) : "--"}
            </Typography>
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
};

export default PeakBookingSlots;
